import { LockSimple, Question, SignOut } from '@/core/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { ROLE_LABELS, startPasswordChange } from '@/core/auth';
import { Badge, cn, Spinner, ThemeSegmented } from '@/core/ui';
import { useCurrentUser, useLogout } from '../application/session';
import { initials, jobLine } from '../domain/user';
import { AccountActionNotice } from './AccountActionNotice';

/**
 * Menu da conta. `onShowTour`: quem monta o layout oferece "Ver tour de
 * novo" (o tour mora em app/onboarding — fora deste contexto).
 */
export function UserMenu({ onShowTour }: { onShowTour?: () => void } = {}) {
  const user = useCurrentUser();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState<'idle' | 'pending' | 'failed'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  /**
   * "Alterar senha": a troca acontece na tela do Keycloak (kc_action
   * UPDATE_PASSWORD), que confirma a senha atual e devolve a pessoa a esta
   * mesma página. Não depende de e-mail — funciona sem SMTP.
   */
  async function changePassword() {
    setChanging('pending');
    try {
      const { pathname, search, hash } = window.location;
      await startPasswordChange(pathname + search + hash, user.email);
    } catch {
      setChanging('failed');
    }
  }

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const name = user.full_name || user.email;

  return (
    <div ref={ref} className="relative">
      <AccountActionNotice />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Conta de ${name}`}
        data-tour="user-menu"
        className="rounded-full p-0.5 transition-colors hover:bg-surface-2"
      >
        <Avatar name={name} large />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-80 glass-strong shadow-float rounded-3xl p-2"
        >
          <div className="flex items-center gap-3 p-3">
            <Avatar name={name} large />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{name}</p>
              <p className="truncate text-xs text-fg-subtle">{user.email}</p>
              <p className="truncate text-xs text-fg-subtle">
                {[jobLine(user), user.tenant.name].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 px-3 pb-3">
            {user.roles.map((r) => (
              <Badge key={r} tone="brand">
                {ROLE_LABELS[r] ?? r}
              </Badge>
            ))}
          </div>
          <div className="mx-1 rounded-2xl bg-surface-2/60 px-3 py-3">
            <p className="mb-2 text-[0.65rem] font-semibold tracking-[0.12em] text-fg-subtle uppercase">
              Tema
            </p>
            <ThemeSegmented />
          </div>
          <div className="space-y-0.5 pt-2">
            <button
              type="button"
              role="menuitem"
              onClick={() => void changePassword()}
              disabled={changing === 'pending'}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-2 disabled:cursor-wait"
            >
              {changing === 'pending' ? <Spinner /> : <LockSimple aria-hidden className="size-4" />}
              Alterar senha
            </button>
            {changing === 'failed' && (
              <p role="alert" className="px-3 pb-1 text-xs text-red-400 light:text-red-600">
                Não foi possível abrir a troca de senha. Tente novamente.
              </p>
            )}
            {onShowTour && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onShowTour();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-2"
              >
                <Question aria-hidden className="size-4" />
                Ver tour de novo
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={logout}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-2"
            >
              <SignOut aria-hidden className="size-4" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Avatar({ name, large }: { name: string; large?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-linear-to-br from-brand-400 to-brand-700 font-semibold text-white',
        large ? 'size-10 text-sm' : 'size-8 text-xs',
      )}
    >
      {initials(name)}
    </span>
  );
}
